import { call, put } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { turnMessageOn } from 'store/app/actions';
import { MessageData } from 'store/app/types';
import { logoutRequest } from 'store/auth/actions';
import { ApiError } from 'store/api/types';
import { checkResponse } from 'store/utils';
import { renderErrorMessage } from 'utils/render-error-message';
import {
  viewReconciliationReportPageSuccess,
  viewReconciliationReportComplete,
  viewReconciliationReportFailure,
  fetchReconciliationFiltersRequest,
} from '../actions';
import {
  viewReconciliationReport,
  ViewReconciliationReportResponse,
  loadReconciliationTransactions,
  LoadReconciliationTransactionsResponse,
} from '../api';
import { ViewReconciliationReportRequestAction } from '../types';
import type { ViewReconciliationDataResponse } from '../models';

function normalizeViewReconciliationPayload(
  data: ViewReconciliationDataResponse
): ViewReconciliationDataResponse {
  const nested =
    data.data != null &&
    typeof data.data === 'object' &&
    !Array.isArray(data.data)
      ? (data.data as Record<string, unknown>)
      : null;

  const dataLog =
    data.dataLog ??
    (nested?.dataLog as
      | ViewReconciliationDataResponse['dataLog']
      | undefined) ??
    (nested?.data_log as ViewReconciliationDataResponse['dataLog'] | undefined);

  let reconTotal =
    data.reconTotal ??
    (nested?.reconTotal as
      | ViewReconciliationDataResponse['reconTotal']
      | undefined) ??
    (nested?.recon_total as
      | ViewReconciliationDataResponse['reconTotal']
      | undefined);

  if (reconTotal) {
    reconTotal = {
      ...reconTotal,
      savedStatus: reconTotal.savedStatus ?? null,
    };
  }

  return {
    ...data,
    dataLog: dataLog ?? data.dataLog,
    reconTotal: reconTotal ?? data.reconTotal,
  };
}

export function* viewReconciliationReportSaga(
  action: ViewReconciliationReportRequestAction
) {
  const { organisationId, dataLogId, connectionId, mappingId, currency } = action.payload;
  // `viewReconciliationData` is cleared synchronously in the reducer on
  // VIEW_RECONCILIATION_REPORT_REQUEST before this saga runs.

  try {
    const response: ViewReconciliationReportResponse = yield call(
      viewReconciliationReport,
      organisationId,
      dataLogId
    );
    const err: ApiError | null = checkResponse(response?.responseMessage);
    if (err) throw err;

    const data = normalizeViewReconciliationPayload(response.data);
    yield put(viewReconciliationReportPageSuccess(data));

    // Preload reconciliation transactions so the View buttons have a valid reportId
    let preloadReportId: string | undefined;
    try {
      if (connectionId && mappingId) {
        const reportId = uuidv4().replaceAll('-', '');
        let lastAccrualsId = 1;
        let lastCashAccountingId = 1;
        let isEmpty = false;
        let iterations = 0;
        const MAX_PRELOAD_ITERATIONS = 200;

        do {
          const prevAccrualsId = lastAccrualsId;
          const prevCashAccountingId = lastCashAccountingId;
          const loadResponse: LoadReconciliationTransactionsResponse = yield call(
            loadReconciliationTransactions,
            connectionId,
            mappingId,
            dataLogId,
            currency,
            lastAccrualsId,
            lastCashAccountingId,
            reportId
          );
          const loadErr: ApiError | null = checkResponse(
            loadResponse?.responseMessage
          );
          if (loadErr) throw loadErr;

          if (
            !loadResponse.data &&
            loadResponse.responseMessage?.message === 'Loaded from cached report'
          ) {
            isEmpty = true;
          } else {
            const reportData = loadResponse.data;
            if (
              !reportData ||
              (Object.keys(reportData).length > 0 &&
                (reportData.empty || reportData.recap))
            ) {
              isEmpty = true;
            }
            lastAccrualsId = reportData?.accrualsLastId ?? lastAccrualsId;
            lastCashAccountingId = reportData?.cashAccountingLastId ?? lastCashAccountingId;
            // Defensive stop: if backend keeps returning non-empty pages but
            // pagination cursors do not move, prevent an infinite request loop.
            if (
              !isEmpty &&
              lastAccrualsId === prevAccrualsId &&
              lastCashAccountingId === prevCashAccountingId
            ) {
              isEmpty = true;
            }
          }
          iterations += 1;
          if (iterations >= MAX_PRELOAD_ITERATIONS) {
            isEmpty = true;
          }
        } while (!isEmpty);

        preloadReportId = reportId;

        // Fetch mapping codes so the exclude-Other filter can be built for View buttons
        yield put(fetchReconciliationFiltersRequest(connectionId, mappingId));
      }
    } catch {
      // Preload failed — complete without a reportId so the view still renders
    }

    yield put(viewReconciliationReportComplete(preloadReportId));
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    yield put(viewReconciliationReportFailure());
  }
}
